import { useEffect, useState } from "react";

import useStateHandler from "@/hooks/useStateHandler";
import UserService from "@/services/user.service";
import GenericPopup from "@/shared-components/popups/components/GenericPopup";

import ImageCropper from "./ImageCropper";

import "./styles/PopupUpdateProfilePicture.css";

const PopupUpdateProfilePicture = ({
  open,
  image,
  exitCrop,
  cropAreaClass,
  setCroppedUrl,
  mode = "profile",
  aspect,
  cropShape: cropShapeOverride,
  continueLabel,
  title,
  instructions,
  onSave,
}) => {
  const { state, stateHandler } = useStateHandler();

  const userType = state?.user_session?.user_type;
  const isCoverMode = mode === "cover";
  const cropShape = cropShapeOverride
    || (isCoverMode ? "rect" : (userType === 'gallery' ? 'rect' : 'circle'));
  const resolvedAspect = aspect || (isCoverMode ? 3 / 2 : null);
  const headerTitle = title || (isCoverMode ? "Set cover background" : "Update Profile Picture");
  const headerInstructions = instructions || (isCoverMode
    ? "Crop a portion of this artwork to use as your gallery cover."
    : "Adjust and crop your profile picture");
  const continueLabelText = continueLabel || (isCoverMode ? "Set cover" : "Choose");
  const successMessage = isCoverMode
    ? "Cover background updated successfully!"
    : "Profile picture updated successfully!";
  const errorMessage = isCoverMode
    ? "There was an error updating your cover. Please try again."
    : "There was an error updating your profile picture. Please try again.";

  const [imageFile, setImageFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const updateProfilePicture = async (imageData) => {
    await UserService.editProfile({ 
      ...state.user_session, 
      user_image: imageData 
    });

    stateHandler.set("user_session", { 
      ...state.user_session, 
      user_image: imageData 
    });
  };

  const updateCoverImage = async (imageData) => {
    const coverKey = userType === "artist" ? "artist_cover_image" : "gallery_cover_image";

    await UserService.editProfile({ 
      ...state.user_session, 
      [coverKey]: imageData 
    });

    stateHandler.set("user_session", { 
      ...state.user_session, 
      [coverKey]: imageData 
    });
  };

  const handleProfilePictureUpdate = async (croppedImage) => {
    setIsLoading(true);

    try {
      const { response, data } = await UserService.uploadImage(croppedImage);

      if (response.ok) {
        const performSave = onSave || (isCoverMode ? updateCoverImage : updateProfilePicture);
        await performSave(data);

        stateHandler.set("temporalPopup", {
          text: successMessage,
          type: "navbar-present"
        });

        exitCrop();
      } else {
        alert(errorMessage);
      }
    } catch (err) {
      console.error("Error updating profile image", err);
      alert(errorMessage);
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (imageFile) {
      handleProfilePictureUpdate(imageFile);
    }
  }, [imageFile]);

  return (
    <GenericPopup
      open={open}
      className={`profile-picture__crop-popup ${cropAreaClass}`}
    >
      <div className="profile-picture__header" >
        <p className="profile-picture__header-text">
          {headerTitle}
        </p>
        <p className="profile-picture__instructions">
          {headerInstructions}
        </p>
      </div>
      <ImageCropper
        source={image}
        width={200}
        height={200}
        cropShape={cropShape}
        aspect={resolvedAspect}
        exitCrop={exitCrop}
        setCroppedFile={setImageFile}
        setCroppedUrl={setCroppedUrl}
        isLoading={isLoading}
        continueLabel={continueLabelText}
      />
    </GenericPopup>
  );
};

export default PopupUpdateProfilePicture;
